import { SkeletonLoader, TooltipTrigger } from '@design';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useFilteredInsights } from '@terminal/react-query-hooks/insights/useFilteredInsights';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { ClickableScenarioInsightTooltip } from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/clickable-scenario-insight-tooltip/ClickableScenarioInsightTooltip';
import { InsightsIcon } from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-icon/InsightsIcon';
import { startOfDay, subWeeks } from 'date-fns';
import React, { useMemo } from 'react';

import styles from './CompetitorsSection.module.scss';

const MAX_SCENARIOS = 3;

export interface CompetitorScenariosCellProps {
  entityTag: string;
  source: PosthogTrackingSource;
}

export const CompetitorScenariosCell = ({
  entityTag,
  source,
}: CompetitorScenariosCellProps) => {
  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const { data, isFetching } = useFilteredInsights({
    entity: [entityTag],
    from_date: fromDate,
  });

  if (isFetching) {
    return (
      <SkeletonLoader
        className={styles.styledSkeletonLoaderWrapper}
        areas={[
          { styles: { height: '20px', width: '100%', borderRadius: '8px' } },
        ]}
      />
    );
  }

  const scenarioInsights = data?.data?.result ?? [];
  const exceedsMaxScenarios = scenarioInsights.length > MAX_SCENARIOS;

  const visibleScenarioInsights = scenarioInsights.slice(0, MAX_SCENARIOS);

  const remainingScenarioInsights = exceedsMaxScenarios
    ? scenarioInsights.slice(MAX_SCENARIOS)
    : [];

  return (
    <div className={styles.insightsContainer}>
      {visibleScenarioInsights.map(insight => (
        <InsightsIcon
          key={insight.id}
          insight={insight}
          isDisabled={false}
          variant="assets-table"
          source={source}
        />
      ))}
      {!!remainingScenarioInsights.length && (
        <TooltipContainer
          label={
            <ClickableScenarioInsightTooltip
              insights={remainingScenarioInsights}
              entityTag={entityTag}
              source={source}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
        >
          <div className={styles.insightCount}>
            +{remainingScenarioInsights.length}
          </div>
        </TooltipContainer>
      )}
    </div>
  );
};
